<script setup lang="ts">
import BackstageButton from '@/components/common/BackstageButton.vue';
import type { FailedRow } from '@/features/excel-order-upload/types/excel-template-upload.types';
import { ModalHeaderType } from '@/features/excel-order-upload/types/modal-header.enums';
import { ButtonVariant } from '@/models/button-variant.enum';
import { Icons } from '@/models/icons.enum';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ModalContainer from './ModalContainer.vue';
import ModalHeader from './ModalHeader.vue';

interface Props {
  failedRows: FailedRow[];
  successCount: number;
  totalCount: number;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  close: [];
  discard: [];
  continue: [];
  download: [];
}>();

const { t } = useI18n();

const groupedByReason = computed(() => {
  const map = new Map<string, string[]>();
  for (const row of props.failedRows) {
    for (const reason of row.reasons) {
      const existing = map.get(reason);
      if (existing) {
        existing.push(row.itemNumber);
      } else {
        map.set(reason, [row.itemNumber]);
      }
    }
  }

  return map;
});

const onClickCopy = async () => {
  const plainLines: string[] = [];
  const htmlParts: string[] = ['<div>'];

  for (const [reason, items] of groupedByReason.value) {
    plainLines.push(reason, ...items, '');
    const lists = items.map((i) => `<li>${i}</li>`).join('');
    htmlParts.push(`<p><strong>${reason}</strong></p><ul>${lists}</ul>`);
  }

  htmlParts.push('</div>');

  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/plain': new Blob([plainLines.join('\n').trimEnd()], { type: 'text/plain' }),
        'text/html': new Blob([htmlParts.join('')], { type: 'text/html' }),
      }),
    ]);
  } catch {
    await navigator.clipboard.writeText(plainLines.join('\n').trimEnd());
  }
};
</script>

<template>
  <ModalContainer @close="emit('close')">
    <template #header>
      <ModalHeader
        :title="t('excel_order_upload.partial_success_modal.title')"
        :description="t('excel_order_upload.partial_success_modal.description')"
        :icon="Icons.BLUE_INFO"
        :type="ModalHeaderType.INFO" />
    </template>
    <template #main>
      <div class="partial-success-body">
        <div v-for="[reason, items] in groupedByReason" :key="reason">
          <p class="partial-success-body__reason">{{ reason }}</p>
          <ul class="partial-success-body__item-list">
            <li v-for="item in items" :key="item" class="partial-success-body__item">{{ item }}</li>
          </ul>
        </div>
      </div>
      <div class="partial-success-cta">
        <BackstageButton :variant="ButtonVariant.SECONDARY" @click="onClickCopy" :icon-right="Icons.COPY">{{
          t('excel_order_upload.partial_success_modal.button_label.copy')
        }}</BackstageButton>
        <BackstageButton
          :variant="ButtonVariant.SECONDARY"
          @click="emit('download')"
          :icon-right="Icons.DOWNLOAD_ARROW_SIGN"
          class="partial-success-cta__download"
          >{{ t('excel_order_upload.partial_success_modal.button_label.download') }}</BackstageButton
        >
      </div>
    </template>
    <template #footer>
      <BackstageButton :variant="ButtonVariant.SECONDARY" :full-width="true" @click="emit('discard')">{{
        t('excel_order_upload.partial_success_modal.button_label.discard')
      }}</BackstageButton>
      <BackstageButton :variant="ButtonVariant.PRIMARY" :full-width="true" @click="emit('continue')">{{
        t('excel_order_upload.partial_success_modal.button_label.proceed')
      }}</BackstageButton>
    </template>
  </ModalContainer>
</template>

<style lang="scss" scoped>
.partial-success-body {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  row-gap: 12px;
  width: 100%;
  max-height: 400px;
  overflow-y: auto;

  &__reason {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.5;
    text-align: left;
  }

  &__item-list {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__item {
    margin: 0;
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    text-align: left;
  }
}

.partial-success-cta {
  display: flex;
  column-gap: 20px;
  margin-top: 12px;

  &__download:deep(.icon.right) {
    background-color: var(--primary-text-color);
  }
}
</style>
